import { Anchor, Button, Menu } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconDotsVertical } from "@tabler/icons-react"
import { AreYouSureModal } from "@tfg/ui"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import { LibraryResponses } from "@tfg/types/endpoints/library"

import { queryClient } from "~/backend/queryClient"
import { useAnonymizeUser } from "~/gen/api"
import { ToggleUserStatusButton } from "~/pages/Admin/Organizations/ToggleUserStatusButton/ToggleUserStatusButton"
import { useToggleUserActiveStatusMutation } from "~/pages/Admin/Organizations/hooks/useToggleUserStatusHook"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

type UserOptionsProps = {
  organization: Pick<AdminResponses.OrganizationDetails, "id">
  user: Omit<LibraryResponses.TFGUser, "organization">
}

const NOTIFICATIONS = {
  SUCCESS: {
    message: `User anonymized`,
    color: "green",
  },
  ERROR: {
    title: "Error",
    message: "An error occurred while anonymizing the user",
    color: "red",
  },
}

export const UserOptions = ({ organization, user }: UserOptionsProps) => {
  const { mutate: anonymizeUser } = useAnonymizeUser({
    mutation: {
      onSuccess: () => {
        showSuccessNotification({
          message: NOTIFICATIONS.SUCCESS.message,
        })
        queryClient.invalidateQueries({
          queryKey: ["organizations", organization.id],
        })
        return true
      },
      onError: (error: unknown) => {
        showAxiosError({
          error: error as Error,
          notificationTitle: NOTIFICATIONS.ERROR.title,
          notificationMessage: NOTIFICATIONS.ERROR.message,
          showNotification: true,
        })
      },
    },
  })
  const [anonymizeOpened, { open: openAnonymize, close: closeAnonymize }] =
    useDisclosure(false)
  const [toggleOpened, { open: openToggle, close: closeToggle }] =
    useDisclosure(false)

  const { mutateAsync: toggleUserStatus } = useToggleUserActiveStatusMutation()

  const handleToggleUserStatus = async (isActive: boolean) => {
    await toggleUserStatus({
      ...user,
      active: isActive,
      organizationId: organization.id,
    })
    queryClient.invalidateQueries({
      queryKey: ["organizations", organization.id],
    })
  }
  const handleConfirmToggle = async () => {
    await handleToggleUserStatus(!user.active)
    closeToggle()
  }

  return (
    <>
      <AreYouSureModal
        opened={anonymizeOpened}
        onClose={closeAnonymize}
        onConfirm={() => anonymizeUser(undefined as never)}
        areYouSureText={`Are you sure you want to anonymize ${user.name}?`}
        confirmText="Anonymize"
      />
      <AreYouSureModal
        opened={toggleOpened}
        onClose={closeToggle}
        onConfirm={handleConfirmToggle}
        areYouSureText={`Are you sure you want to ${user.active ? "deactivate" : "activate"} ${user.name}?`}
        confirmText={user.active ? "Deactivate" : "Activate"}
      />
      <Menu>
        <Menu.Target>
          <Anchor>
            <IconDotsVertical size={16} />
          </Anchor>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item>
            <ToggleUserStatusButton
              user={user}
              openModal={openToggle}
              organizationId={organization.id}
            />
          </Menu.Item>
          <Menu.Item>
            <Button
              size="sm"
              variant="secondary"
              fullWidth
              data-user-email={user.email}
              data-testid="anonymize-user"
              onClick={openAnonymize}
            >
              Anonymize
            </Button>
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </>
  )
}
